import { useAppModal } from "@/contexts/AppModal/useAppModal";
import { useIndexDB } from "@/contexts/IndexDB/useIndexDB";
import { useDeviceKey } from "@/hooks/useDeviceKey";
import { deleteCurrentRecording } from "@/utils/deleteCurrentRecording";
import { trackBusinessEvent } from "@/utils/reportEvent";
import { Button, ButtonSeverity, ButtonVariant, DeleteIcon, HorizontalFlex, MediumTextM, Modal, ModalSize, Space, VerticalFlex } from "@enxcs/dap-ui-stratos";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "@/hooks/useErrorHandler";

export interface DeleteFailedRecordingModalProps {
  recordingId: string;
}

const DeleteFailedRecordingModal: FC<DeleteFailedRecordingModalProps> = ({ recordingId }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const { onCloseModal } = useAppModal();

  const { errorHandler } = useErrorHandler();

  const deviceKey = useDeviceKey();

  const { deleteFromIndexDB } = useIndexDB();

  const queryClient = useQueryClient();

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);

      // Keep local failed recording data available unless backend deletion succeeds.
      await deleteCurrentRecording(deviceKey, recordingId);
      try {
        await deleteFromIndexDB([recordingId]);
      } catch (localDeleteError) {
        console.warn(`DeleteFailedRecordingModal: backend delete succeeded, local cleanup failed for ${recordingId}`, localDeleteError);
      }

      await queryClient.invalidateQueries({ queryKey: ["recordingSubjects"] });
      trackBusinessEvent("MICROPHONE_CAPTURE_DISCARDED", { RecordingId: recordingId });

      onCloseModal();
    } catch (error) {
      await errorHandler(error, {
        eventName: "DELETE-RECORDING",
        showToast: true,
        properties: {
          endpoint: "recording/:recordingId",
          reason: `Error Deleting recording - ${error}`,
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      size={ModalSize.M}
      title={t("deleteRecording.failedUploadChunkModalTitle")}
      closeTitle={t("buttons.close")}
      body={
        <VerticalFlex $gap={Space.V8} $justify="space-between" $align={"flex-start"}>
          <MediumTextM>{t("deleteRecording.failedUploadChunkModalMessage")}</MediumTextM>
        </VerticalFlex>
      }
      footer={
        <HorizontalFlex $justify="flex-end" $gap={Space.V8}>
          <Button variant={ButtonVariant.SECONDARY} onClick={onCloseModal} label={t("buttons.cancel")} />
          <Button
            variant={ButtonVariant.PRIMARY}
            disabled={isDeleting}
            severity={ButtonSeverity.DANGER}
            label={t("buttons.delete")}
            onClick={handleConfirmDelete}
            leftIcon={DeleteIcon}
          />
        </HorizontalFlex>
      }
      onClose={() => {
        onCloseModal();
      }}
    />
  );
};

export default DeleteFailedRecordingModal;
